import classNames from 'classnames';
import { FunctionComponent } from 'react';

import { MenuItem } from 'src/components/common/menu-item/MenuItem';
import { NAV_ITEM_TYPE, NAV_PLACEMENT } from 'src/enums/navigation';
import { RESOURCE_BUNDLES } from 'src/enums/resource-bundles';
import { THEME } from 'src/enums/theme';
import cloudsImage from 'src/images/clouds.png';
import { NavItem } from 'src/interfaces/navigation';
import { hasSubNavSidebar, isSidebarItem } from 'src/utils/nav-items';

import styles from './SubNav.module.scss';

interface SubNavProps {
  subNavParent: NavItem;
  isAuthenticated: boolean;
  theme: THEME;
  resourceBundle: RESOURCE_BUNDLES;
  onMouseEnter?: () => void;
  onBlurClick?: () => void;
}

/**
 * Desktop SubNav component that renders either a standard subnav (grid + image)
 * or an enhanced subnav with a sidebar section (e.g. Carbon credit service).
 * The layout is determined automatically based on whether the subNavParent
 * contains sidebar-type children.
 */
export const SubNav: FunctionComponent<SubNavProps> = ({
  subNavParent,
  isAuthenticated,
  theme,
  resourceBundle,
  onMouseEnter,
  onBlurClick,
}) => {
  const hasSidebar = hasSubNavSidebar(subNavParent);
  const sidebarItems = subNavParent.children.filter(isSidebarItem);
  const gridItems = hasSidebar ? subNavParent.children.filter((item) => !isSidebarItem(item)) : subNavParent.children;

  // Filter out the CTA for authenticated users
  const visibleSidebarItems = sidebarItems.filter(
    (item) => item.itemType !== NAV_ITEM_TYPE.SUBNAV_SIDEBAR_CTA || !isAuthenticated
  );

  return (
    <div
      data-testid="subnav-container"
      className={classNames(styles.subNavContainer, styles[theme])}
      onMouseEnter={onMouseEnter}
    >
      {(theme === THEME.REBRAND_DARK || theme === THEME.REBRAND_LIGHT) && (
        <button type="button" className={styles.bluredBackground} onClick={onBlurClick} />
      )}
      {hasSidebar && (
        <div className={styles.subNavSidebar}>
          <ul className={styles.subNavSidebarList}>
            {visibleSidebarItems.map((item) => (
              <MenuItem
                isNavExpanded
                key={item.id}
                navItem={item}
                placement={NAV_PLACEMENT.SUB_NAV}
                theme={theme}
                resourceBundle={resourceBundle}
              />
            ))}
          </ul>
        </div>
      )}

      <ul className={styles.subNavListContainer}>
        {gridItems.map((item) => (
          <MenuItem
            isNavExpanded
            key={item.id}
            navItem={item}
            placement={NAV_PLACEMENT.SUB_NAV}
            theme={theme}
            resourceBundle={resourceBundle}
          />
        ))}
      </ul>

      <img className={styles.subNavImage} src={cloudsImage} alt="" />
    </div>
  );
};
